import React, { useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "organisms/iconCommonent";
import { FaRegEdit } from "react-icons/fa";

interface Nurturing {
  _id: string;
  heading: string;
  description?: string;
  image?: string;
  localVideo?: string;
  video?: string; // YouTube video link
}

export default function Nurturing() {
  const [banner, setBanner] = useState<Nurturing | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Nurturing | null>(null);

  // Fetch banners
  const fetchBanners = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LOCAL_URL}/api/social-changes`
      );
      const selectedBanner = res.data.message[1]; // section 2 data
      setBanner(selectedBanner);
    } catch (error) {
      console.error("Error fetching banner:", error);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleEdit = (banner: Nurturing) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  return (
    <div className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-lato-regular">Section - 02</h2>
        <hr />
      </div>

      {banner && (
        <div className="w-full bg-white border border-gray-200 rounded-xl overflow-hidden">
          {/* Image */}
          {banner.image && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.image}`}
              alt={banner.heading}
              className="w-full h-[24rem] object-cover rounded-t-xl"
            />
          )}

          {/* Video (YouTube or Local — only one) */}
          {banner.video ? (
            <iframe
              width="100%"
              height="400"
              src={banner.video}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="rounded-t-xl"
            ></iframe>
          ) : banner.localVideo ? (
            <video
              src={`${process.env.REACT_APP_LOCAL_URL}/${banner.localVideo}`}
              autoPlay
              loop
              muted
              controls
              className="w-[30rem] h-[20rem] object-cover rounded-t-xl my-4"
            />
          ) : null}

          <div className="p-5">
            <h2
              className="text-xl font-semibold text-black font-lato-regular mb-2"
              dangerouslySetInnerHTML={{ __html: banner.heading }}
            />
            <h4 className="text-base font-lato-regular text-gray-700 mb-3">
              {banner.description}
            </h4>

            <div className="flex justify-start gap-3">
              <button
                className="bg-blue-500 font-lato-regular text-white px-4 py-2 rounded"
                onClick={() => handleEdit(banner)}
              >
                <span className="inline-block mr-1">
                  <Icon icon={FaRegEdit} />
                </span>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && editingBanner && (
        <BannerFormModal
          onClose={() => setShowForm(false)}
          fetchBanners={fetchBanners}
          editingBanner={editingBanner}
        />
      )}
    </div>
  );
}

interface BannerFormModalProps {
  onClose: () => void;
  fetchBanners: () => void;
  editingBanner: Nurturing;
}

const BannerFormModal = ({
  onClose,
  fetchBanners,
  editingBanner,
}: BannerFormModalProps) => {
  const [heading, setHeading] = useState(editingBanner.heading || "");
  const [description, setDescription] = useState(
    editingBanner.description || ""
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeVideo, setYoutubeVideo] = useState<string>(
    editingBanner.video || ""
  );

  // Handle YouTube link change
  const handleYoutubeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeVideo(e.target.value);
    if (e.target.value) {
      setVideoFile(null); // remove local video if YouTube is added
    }
  };

  // Handle local video upload
  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setVideoFile(file);
    if (file) {
      setYoutubeVideo(""); // clear YouTube video when local is uploaded
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim()) return;

    const formData = new FormData();
    formData.append("heading", heading);
    formData.append("description", description);

    // Video logic: send only one
    if (youtubeVideo) {
      formData.append("video", youtubeVideo);
      formData.append("localVideo", ""); // clear local video
    } else if (videoFile) {
      formData.append("localVideo", videoFile);
      formData.append("video", ""); // clear YouTube link
    }

    // Image upload
    if (imageFile) formData.append("image", imageFile);

    try {
      await axios.put(
        `${process.env.REACT_APP_LOCAL_URL}/api/social-changes/${editingBanner._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      fetchBanners();
      onClose();
    } catch (error) {
      console.error("Error updating banner:", error);
      alert("Failed to update banner. Check console for details.");
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg overflow-y-auto max-h-[90vh]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block font-lato-bold">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            required
            className="w-full border border-gray-300 rounded-md p-2"
          />

          <label className="block font-lato-bold">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2"
            rows={3}
          />

          {/* YouTube Video */}
          <label className="block font-lato-bold">YouTube Video Link</label>
          <input
            type="text"
            value={youtubeVideo}
            onChange={handleYoutubeChange}
            placeholder="https://www.youtube.com/embed/your-video-id"
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Preview YouTube Video */}
          {youtubeVideo && (
            <iframe
              src={youtubeVideo}
              title="YouTube Preview"
              className="w-full h-48 rounded-md mt-2"
              allowFullScreen
            ></iframe>
          )}

          {/* Image Section */}
          {editingBanner.image && !imageFile && (
            <img
              src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.image}`}
              alt="banner"
              className="w-32 h-32 object-cover rounded-md mb-2"
            />
          )}
          <label className="block font-lato-bold">Upload Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            className="w-full border border-gray-300 rounded-md p-2"
          />

          {/* Local Video (only shown when YouTube is empty) */}
          {!youtubeVideo && (
            <>
              {editingBanner.localVideo && !videoFile && (
                <video
                  src={`${process.env.REACT_APP_LOCAL_URL}/${editingBanner.localVideo}`}
                  controls
                  muted
                  loop
                  className="w-full h-40 object-cover rounded-md mb-2"
                />
              )}
              <label className="block font-lato-bold">Upload Local Video</label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="w-full border border-gray-300 rounded-md p-2"
              />
            </>
          )}

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Update
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
